import { useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Mail, Globe, Download, Share2, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COMPANY = {
  name: "SauberEko s. r. o.",
  address: "Ivana Krasku 25/9, 972 71 Nováky",
  ico: "57 356 050",
  dic: "2122719995",
  icdph: "SK2122719995",
};

const PEOPLE = {
  timea: {
    first: "Timea", last: "Smrktová", name: "Timea Smrktová",
    titleSk: "Riaditeľka pre marketing a styk s verejnosťou",
    titleEn: "Director of Marketing & Public Relations",
    email: "timeasmrktova@saubereko.eu",
  },
  martin: {
    first: "Martin", last: "Smrkta", name: "Martin Smrkta",
    titleSk: "Zakladateľ SauberEko® a konateľ SauberEko s. r. o.",
    titleEn: "Founder of SauberEko® & Managing Director",
    email: "martinsmrkta@saubereko.eu",
  },
};

const WEB = "www.saubereko.eu";
const WEB_URL = "https://saubereko.eu";

function buildVCard(p) {
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${p.last};${p.first};;;`,
    `FN:${p.name}`,
    `ORG:${COMPANY.name}`,
    `TITLE:${p.titleSk} / ${p.titleEn}`,
    `EMAIL;TYPE=WORK:${p.email}`,
    `URL:${WEB_URL}`,
    `ADR;TYPE=WORK:;;${COMPANY.address};;;;`,
    `NOTE:IČO ${COMPANY.ico} · DIČ ${COMPANY.dic} · IČ DPH ${COMPANY.icdph}`,
    "END:VCARD",
  ].join("\r\n");
}

export default function Vizitka() {
  const { slug } = useParams();
  const key = PEOPLE[slug] ? slug : "timea";
  const p = PEOPLE[key];
  const [copied, setCopied] = useState(false);

  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/vizitka/${key}` : WEB_URL;
  const shareUrl = `${BACKEND}/api/vizitka/${key}`; // OG preview + redirect

  const saveContact = () => {
    const blob = new Blob([buildVCard(p)], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${p.first}-${p.last}-SauberEko.vcf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${p.name} — SauberEko®`, text: p.titleSk, url: shareUrl }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
    }
  };

  return (
    <div
      data-testid="vizitka-page"
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{ background: "radial-gradient(1200px 600px at 50% -10%, #1a1712 0%, #0b0b0c 55%, #060606 100%)" }}
    >
      <div
        className="relative w-full max-w-md bg-[#0d0d0f] border border-[#D4AF37]/30"
        style={{ boxShadow: "0 0 0 1px rgba(212,175,55,0.08), 0 40px 90px -30px rgba(0,0,0,0.95)" }}
      >
        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="px-8 pt-10 pb-8 text-center">
          <img src="/saubereko-logo.png" alt="SauberEko®" data-testid="vizitka-logo"
            className="mx-auto w-40 h-auto select-none" draggable={false} />

          <div className="mt-7 mb-1 mx-auto w-16 h-px bg-[#D4AF37]/50" />

          <h1 data-testid="vizitka-name" className="font-display text-3xl sm:text-4xl text-white mt-5 tracking-wide">
            {p.name}
          </h1>

          <p className="text-[#D4AF37] text-sm sm:text-base mt-3 tracking-[0.12em] uppercase">{p.titleSk}</p>
          <p className="text-white/45 text-xs mt-1.5 tracking-[0.18em] uppercase">{p.titleEn}</p>

          <div className="mt-7 mb-7 mx-auto w-16 h-px bg-[#D4AF37]/50" />

          <div className="space-y-3">
            <a href={`mailto:${p.email}`} data-testid="vizitka-email"
              className="flex items-center justify-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors">
              <Mail size={16} className="text-[#D4AF37]" /><span className="text-sm">{p.email}</span>
            </a>
            <a href={WEB_URL} target="_blank" rel="noreferrer" data-testid="vizitka-web"
              className="flex items-center justify-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors">
              <Globe size={16} className="text-[#D4AF37]" /><span className="text-sm">{WEB}</span>
            </a>
          </div>

          <div className="mt-8 flex flex-col items-center">
            <div className="bg-white p-3 rounded-sm" data-testid="vizitka-qr">
              <QRCodeCanvas value={pageUrl} size={128} fgColor="#0b0b0c" bgColor="#ffffff" level="M" />
            </div>
            <p className="text-white/35 text-[11px] mt-2 tracking-wide">Naskenujte pre uloženie vizitky</p>
          </div>

          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <button type="button" onClick={saveContact} data-testid="vizitka-save-contact"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-3 text-sm hover:bg-[#F2E3C6] transition-colors">
              <Download size={16} /> Uložiť kontakt
            </button>
            <button type="button" onClick={share} data-testid="vizitka-share"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors">
              {copied ? <Check size={16} /> : <Share2 size={16} />} {copied ? "Skopírované" : "Zdieľať"}
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 px-8 py-5 text-center">
          <p className="text-white/55 text-xs" data-testid="vizitka-company">{COMPANY.name} · {COMPANY.address}</p>
          <p className="text-white/35 text-[11px] mt-1">IČO {COMPANY.ico} · DIČ {COMPANY.dic} · IČ DPH {COMPANY.icdph}</p>
          <p className="text-[#D4AF37]/60 text-[10px] mt-2 tracking-[0.2em] uppercase">The Mark of True Professionals</p>
        </div>

        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      </div>
    </div>
  );
}
